import { API_CONFIG } from "../config/api";
import {
  ANALYTICS_POLICY,
  type CookieConsentResult,
  type CookieConsentState,
  ESSENTIAL_POLICY,
  NonEssentialVendors,
  type PolicyCategory,
  type SdkPartyDefinition,
  type SdkProductConsent,
  type VendorCatalog,
} from "../types/cookieConsent";

const PRODUCTION_SDK_URL =
  "https://cookie.access-evo.com/consent/access-cookie-policies-js/";

const NON_PRODUCTION_SDK_URL =
  "https://cookie.dev.access-evo.com/consent/access-cookie-policies-js/";

/**
 * Host prefixes that identify a non-production deployment, e.g.
 * `preprod-cashflow.erp.accessacloud.com`.
 */
const NON_PRODUCTION_HOST_PREFIXES = [
  "preprod",
  "staging",
  "integration",
  "dev",
  "test",
  "qa",
  "uat",
  "localhost",
];

const SCRIPT_ID = "access-cookie-policies-sdk";

const SDK_LANGUAGE = "en-GB";

const CONFIGURED_POLICIES = [ESSENTIAL_POLICY, ANALYTICS_POLICY];

/** Legacy/display aliases accepted by `isGranted`. */
const POLICY_ALIASES: Record<string, string> = {
  "essential & security": ESSENTIAL_POLICY,
  "essential and security": ESSENTIAL_POLICY,
  "strictly necessary": ESSENTIAL_POLICY,
  necessary: ESSENTIAL_POLICY,
  "analytics & performance": ANALYTICS_POLICY,
  performance: ANALYTICS_POLICY,
};

// TODO: Confirm the canonical Access privacy-notice URL with product/legal
const ACCESS_PRIVACY_URL =
  "https://www.theaccessgroup.com/en-gb/legal/privacy-notice/";

function isLocalDevelopment(): boolean {
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/**
 * The deployment host encodes the environment. The app normally runs on a
 * `*-cashflow.erp.accessacloud.com` host; when it does not (e.g. embedded under
 * another shell host) the manifest `cookiePolicyUrl` host is used instead.
 */
function resolveEnvironmentHost(policyUrl: string): string {
  const hostname = window.location.hostname;
  if (hostname.includes("cashflow")) {
    return hostname;
  }
  try {
    return new URL(policyUrl).hostname;
  } catch {
    return hostname;
  }
}

function isProductionHost(host: string): boolean {
  const subdomain = host.toLowerCase().split(".")[0] ?? "";
  return !NON_PRODUCTION_HOST_PREFIXES.some(
    (prefix) => subdomain === prefix || subdomain.startsWith(`${prefix}-`),
  );
}

export function resolveSdkUrl(policyUrl: string): string {
  return isProductionHost(resolveEnvironmentHost(policyUrl))
    ? PRODUCTION_SDK_URL
    : NON_PRODUCTION_SDK_URL;
}

function normalisePolicyName(policyName: string): string {
  const normalised = policyName.trim().toLowerCase();
  return POLICY_ALIASES[normalised] ?? normalised;
}

/**
 * Converts the SDK product-consent object into the normalized shape the app
 * consumes. `"*"` grants everything, `null` grants essential only, and an array
 * grants exactly the policies it contains.
 */
export function mapProductConsent(
  productConsent: SdkProductConsent,
): CookieConsentResult {
  const consent: CookieConsentState = {};
  const granted = productConsent?.consent ?? null;

  if (granted === "*") {
    for (const policy of CONFIGURED_POLICIES) {
      consent[policy] = true;
    }
  } else if (granted === null) {
    for (const policy of CONFIGURED_POLICIES) {
      consent[policy] = policy === ESSENTIAL_POLICY;
    }
  } else {
    const grantedPolicies = new Set(
      granted.map((entry) => normalisePolicyName(entry.policy)),
    );
    for (const policy of CONFIGURED_POLICIES) {
      consent[policy] = grantedPolicies.has(policy);
    }
    for (const policy of grantedPolicies) {
      consent[policy] = true;
    }
  }

  return {
    consent,
    isGranted: (policyName: string) =>
      consent[normalisePolicyName(policyName)] ?? false,
  };
}

export function buildVendorCatalog(): VendorCatalog {
  const essential: PolicyCategory = {
    policyName: ESSENTIAL_POLICY,
    vendors: [
      {
        name: "Authentication (OIDC)",
        description:
          "Stores the authenticated user session in sessionStorage (oidc.user:*) to maintain login state.",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
      {
        name: "App Mode",
        description:
          "Remembers the selected application mode (demo/live) in localStorage (app-mode).",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
      {
        name: "Onboarding State",
        description:
          "Tracks onboarding progress in localStorage (onboarding_*) so the assistant resumes where you left off.",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
      {
        name: "User Preferences",
        description:
          "Persists per-user display preferences (theme, landing page) in localStorage (cashflow_user_preferences).",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
      {
        name: "Sidebar State",
        description:
          "Stores sidebar open/collapsed state in a cookie (sidebar_state) to preserve layout across pages.",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
      {
        name: "Terminology",
        description:
          "Caches the organisation's custom terminology configuration in sessionStorage (system-terminology-config).",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
      {
        name: "Jurisdiction Selection",
        description:
          "Stores selected jurisdiction and settings in localStorage (cashflow_jurisdictions, cashflow_settings, cashflow_selected_jurisdiction_id) for multi-jurisdiction support.",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
    ],
  };

  const analytics: PolicyCategory = {
    policyName: ANALYTICS_POLICY,
    vendors: [
      {
        name: NonEssentialVendors.WOOTRIC,
        key: NonEssentialVendors.WOOTRIC,
        description:
          "In-app NPS survey tool used to collect user satisfaction feedback.",
        vendorUrl: "https://wootric.com/privacy-policy/",
        partyType: "Third Party",
      },
      {
        name: NonEssentialVendors.FEATURE_FEEDBACK_COLLECTOR,
        key: NonEssentialVendors.FEATURE_FEEDBACK_COLLECTOR,
        description:
          "Collects per-feature sentiment feedback to help prioritise product improvements.",
        vendorUrl: ACCESS_PRIVACY_URL,
        partyType: "First Party",
      },
      {
        name: NonEssentialVendors.INTERCOM,
        key: NonEssentialVendors.INTERCOM,
        description:
          "Customer messaging platform for in-app support and communication (live mode only).",
        vendorUrl: "https://www.intercom.com/legal/privacy",
        partyType: "First Party",
      },
    ],
  };

  return { essential, analytics };
}

function buildPartyEntry(category: PolicyCategory): SdkPartyDefinition {
  return {
    policy: category.policyName,
    vendors: category.vendors.map((vendor) => ({
      name: vendor.name,
      key: vendor.key ?? vendor.name,
      description: vendor.description,
      vendorUrl: vendor.vendorUrl,
      partyType: vendor.partyType,
    })),
  };
}

export function buildParties(catalog: VendorCatalog): SdkPartyDefinition[] {
  return [
    buildPartyEntry(catalog.essential),
    buildPartyEntry(catalog.analytics),
  ];
}

function loadScript(sdkUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = sdkUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Access Cookie Policies SDK"));
    document.head.appendChild(script);
  });
}

function waitForSdk(attempts = 0): Promise<typeof window.AccessCookiePolicies> {
  const maxAttempts = 20;
  const retryDelay = 500;

  return new Promise((resolve, reject) => {
    if (window.AccessCookiePolicies?.SDK) {
      resolve(window.AccessCookiePolicies);
      return;
    }

    if (attempts >= maxAttempts) {
      reject(
        new Error("Access Cookie Policies SDK did not initialise in time"),
      );
      return;
    }

    setTimeout(() => {
      waitForSdk(attempts + 1).then(resolve, reject);
    }, retryDelay);
  });
}

function createGrantAllConsent(): CookieConsentResult {
  const consent: CookieConsentState = {
    [ESSENTIAL_POLICY]: true,
    [ANALYTICS_POLICY]: true,
  };
  return {
    consent,
    isGranted: (policyName: string) =>
      consent[normalisePolicyName(policyName)] ?? true,
  };
}

interface LoadCookieSdkOptions {
  policyUrl: string;
}

export async function loadCookieSdk(
  options: LoadCookieSdkOptions,
): Promise<CookieConsentResult> {
  if (isLocalDevelopment()) {
    return createGrantAllConsent();
  }

  await loadScript(resolveSdkUrl(options.policyUrl));
  const acp = await waitForSdk();

  if (!acp?.SDK) {
    throw new Error("Access Cookie Policies SDK not available");
  }

  const catalog = buildVendorCatalog();

  const productConsent = await acp.SDK.tabInitialise({
    config: {
      policies: [...CONFIGURED_POLICIES],
      parties: buildParties(catalog),
      applicationId: API_CONFIG.cookieApplicationId,
      policyVersion: API_CONFIG.cookiePolicyVersion,
      policyUrl: options.policyUrl,
    },
    language: SDK_LANGUAGE,
  });

  return mapProductConsent(productConsent);
}

/**
 * Reads the consent cookie, handling .NET-style chunking where a cookie named
 * `access-evo-cc.<experience>` may be split into `C1`, `C2`, etc. suffixes.
 */
export function readConsentCookie(
  experience: string,
): CookieConsentState | null {
  const baseName = `access-evo-cc.${experience}`;
  const cookies = document.cookie
    .split(";")
    .reduce<Record<string, string>>((acc, c) => {
      const [key, ...rest] = c.trim().split("=");
      if (key) {
        acc[key] = rest.join("=");
      }
      return acc;
    }, {});

  // Try single cookie first
  if (cookies[baseName]) {
    try {
      return JSON.parse(
        decodeURIComponent(cookies[baseName]),
      ) as CookieConsentState;
    } catch {
      return null;
    }
  }

  // Try chunked cookies (C1, C2, ...)
  let assembled = "";
  let chunk = 1;
  while (cookies[`${baseName}C${chunk}`] !== undefined) {
    assembled += cookies[`${baseName}C${chunk}`];
    chunk++;
  }

  if (!assembled) {
    return null;
  }

  try {
    return JSON.parse(decodeURIComponent(assembled)) as CookieConsentState;
  } catch {
    return null;
  }
}
